import type { RouteLocationNormalized } from "vue-router";
import type { AuthRequirement } from "../types";
import { AuthRequirementBuilder, hasAccess } from "../utils";
import type { PermissionGrants } from "~/layers/roles/server/core/types";

const protectedRoutes: {
  pattern: RegExp;
  excludePattern?: RegExp;
  requirement: AuthRequirement;
}[] = [
  {
    pattern: /^\/admin\/*.*$/,
    excludePattern: /^\/admin\/auth\/*.*$/,
    requirement: AuthRequirementBuilder.authenticated(),
  },
];

export default defineNuxtRouteMiddleware(async (to, from) => {
  const { getSession, status } = useAuth();
  const session = await getSession();

  const fromRouteAuth = getRouteAuth(from);
  const toRouteAuth = getRouteAuth(to);

  const viewer = {
    isAuthenticated: status.value === "authenticated",
    grants: session.user?.role.grants ?? {},
  };

  const isFromRouteAvailable = hasAccess(viewer, fromRouteAuth);
  const isToRouteAvailable = hasAccess(viewer, toRouteAuth);

  if (!isToRouteAvailable && !viewer.isAuthenticated) {
    return navigateTo("/admin/auth/");
  }

  if (!isToRouteAvailable) {
    if (isFromRouteAvailable) return abortNavigation();

    return navigateTo("/admin/");
  }
});

function getRouteAuth(route: RouteLocationNormalized) {
  const routeSettings = protectedRoutes.find(
    (protectedRoute) =>
      protectedRoute.pattern.test(route.fullPath) &&
      !protectedRoute.excludePattern?.test(route.fullPath)
  );

  const routeAuth: AuthRequirement = AuthRequirementBuilder.and(
    routeSettings?.requirement,
    route.meta.auth?.requirement
  );

  return routeAuth;
}
